import { readFromEnv, Env } from '@cinch-nx/environments'
import axios from 'axios'
import { datadogRum } from '@datadog/browser-rum'
import { BookingRequest } from '../../types'
import {
  AvailableDatesRequest,
  AvailableDatesResponse,
  AvailableTimeSlotRequest,
  AvailableTimeSlotsResponse,
  GetSitesResponse,
  SendServicingEnquiryRequest,
} from './types'

const cinchServicingClient = axios.create({
  baseURL: readFromEnv(Env.CinchServicingUrl),
  validateStatus: () => true,
})

export const getSites = async () => {
  try {
    const response = await cinchServicingClient.get<GetSitesResponse>('/sites')

    if (
      response &&
      response.status === 200 &&
      response.data &&
      response.data.sites.length > 0
    ) {
      datadogRum.addAction('Cinch Servicing: Get sites - Success', {
        cinchServicing: true,
        apiEndpoint: '/sites',
        statusCode: response.status,
      })

      return response.data.sites
    } else {
      datadogRum.addError(new Error('Cinch Servicing: Get sites - Failure'), {
        cinchServicing: true,
        apiEndpoint: '/sites',
        statusCode: response.status,
      })
      return
    }
  } catch (error) {
    datadogRum.addError(new Error('Cinch Servicing: Get sites - Error'), {
      cinchServicing: true,
      apiEndpoint: '/sites',
      error: error,
    })
    return
  }
}

export const sendEnquiry = async (request: SendServicingEnquiryRequest) => {
  try {
    const { status } = await cinchServicingClient.post('/enquiries', request)

    if (status === 200) {
      datadogRum.addAction(
        'Cinch Servicing: Send Servicing Enquiry - Success',
        {
          cinchServicing: true,
          apiEndpoint: '/enquiries',
          serviceOptions: request.enquiry.serviceOptions.join(', '),
          store: request.enquiry.storeName,
          statusCode: status,
        },
      )

      return true
    } else {
      datadogRum.addError(
        new Error('Cinch Servicing: Send Servicing Enquiry - Failure'),
        {
          cinchServicing: true,
          apiEndpoint: '/enquiries',
          serviceOptions: request.enquiry.serviceOptions.join(', '),
          store: request.enquiry.storeName,
          statusCode: status,
        },
      )
    }

    return false
  } catch (error) {
    datadogRum.addError(
      new Error('Cinch Servicing: Send Servicing Enquiry - Error'),
      {
        cinchServicing: true,
        apiEndpoint: '/enquiries',
        serviceOptions: request.enquiry.serviceOptions.join(', '),
        store: request.enquiry.storeName,
        error: error,
      },
    )
    return false
  }
}

export const getAvailableDates = async (request: AvailableDatesRequest) => {
  try {
    const response = await cinchServicingClient.get<AvailableDatesResponse>(
      '/bookings/available-dates',
      {
        params: {
          'dealer-id': request.dealerId,
          'service-options': mapServiceOptions(request.serviceOptions),
          'appointment-type': mapAppointmentType(request.appointmentType),
        },
      },
    )

    if (response && response.data && response.status === 200) {
      datadogRum.addAction('Cinch Servicing: Get available dates - Success', {
        cinchServicing: true,
        apiEndpoint: '/bookings/available-dates',
        statusCode: response.status,
      })

      return response.data
    } else {
      datadogRum.addError(
        new Error('Cinch Servicing: Get available dates - Failure'),
        {
          cinchServicing: true,
          apiEndpoint: '/bookings/available-dates',
          statusCode: response.status,
        },
      )
      return []
    }
  } catch (error) {
    datadogRum.addError(
      new Error('Cinch Servicing: Get available dates - Error'),
      {
        cinchServicing: true,
        apiEndpoint: '/bookings/available-dates',
        error: error,
      },
    )

    throw error
  }
}

export const getAvailableTimeSlots = async (
  request: AvailableTimeSlotRequest,
) => {
  try {
    const response = await cinchServicingClient.get<AvailableTimeSlotsResponse>(
      '/bookings/available-time-slots',
      {
        params: {
          'dealer-id': request.dealerId,
          'appointment-type-id': request.appointmentTypeId,
          date: request.date,
        },
      },
    )

    if (response && response.data && response.status === 200) {
      datadogRum.addAction(
        'Cinch Servicing: Get available time slots - Success',
        {
          cinchServicing: true,
          apiEndpoint: '/bookings/available-time-slots',
          statusCode: response.status,
        },
      )

      return response.data
    } else {
      datadogRum.addError(
        new Error('Cinch Servicing: Get available time slots - Failure'),
        {
          cinchServicing: true,
          apiEndpoint: '/bookings/available-time-slots',
          statusCode: response.status,
        },
      )

      return
    }
  } catch (error) {
    datadogRum.addError(
      new Error('Cinch Servicing: Get available time slots - Error'),
      {
        cinchServicing: true,
        apiEndpoint: '/bookings/available-time-slots',
        error: error,
      },
    )
    throw Error
  }
}

export const createBooking = async (bookingRequest: BookingRequest) => {
  try {
    const response = await cinchServicingClient.post(
      '/bookings',
      bookingRequest,
    )

    if (response && response.data && response.status === 201) {
      datadogRum.addAction('Cinch Servicing: Create booking - Success', {
        cinchServicing: true,
        apiEndpoint: '/bookings',
        statusCode: response.status,
      })

      return true
    } else {
      datadogRum.addError(
        new Error('Cinch Servicing: Create booking - Failure'),
        {
          cinchServicing: true,
          apiEndpoint: '/bookings',
          statusCode: response.status,
        },
      )

      return false
    }
  } catch (error) {
    datadogRum.addError(new Error('Cinch Servicing: Create booking - Error'), {
      cinchServicing: true,
      apiEndpoint: '/bookings',
      error: error,
    })
    return false
  }
}

const mapServiceOptions = (serviceOptions: string | undefined) => {
  if (!serviceOptions) {
    return ''
  }

  return serviceOptions
    .split(', ')
    .map((serviceOption) => {
      switch (serviceOption) {
        case 'MOT': {
          return 1
        }
        case 'Oil & filter change': {
          return 2
        }
        case 'Standard service': {
          return 3
        }
        case 'Standard EV service': {
          return 3
        }
        case 'Full service': {
          return 4
        }
        case 'Full EV service': {
          return 4
        }
        default: {
          return 1
        }
      }
    })
    .join(',')
}

const mapAppointmentType = (appointmentType: string) =>
  appointmentType === 'Drop and go' ? 'drop-go' : 'drop-wait'
